<template>
  <div class="connected-centers">
    <div
      class="tw-flex tw-items-center tw-justify-between tw-mb-2"
      data-test="connected-centers-header"
    >
      <label class="tw-mb-0">
        {{ trans("users.connectedCentersHeader") }}
      </label>

      <div v-if="!isEditing">
        <button
          type="button"
          class="btn btn-primary add-center-button"
          data-test="connected-centers-add"
          :disabled="!canManage"
          @click="startEditing"
        >
          {{ trans("users.connectedCentersManage") }}
        </button>
      </div>
      <div v-else class="tw-space-x-1">
        <button
          type="button"
          class="btn btn-grey connected-centers-cancel"
          data-test="connected-centers-cancel"
          @click="cancelEditing"
        >
          {{ trans("general.modalCancelButton") }}
        </button>
        <button
          type="button"
          class="btn btn-primary connected-centers-save"
          data-test="connected-centers-save"
          @click="saveSelection"
        >
          {{ trans("general.formSaveLabel") }}
        </button>
      </div>
    </div>

    <div
      v-if="isEditing"
      class="form-group"
      data-test="connected-centers-select"
    >
      <v-select
        label="name"
        multiple
        name="connectedCenterPicker"
        :options="availableOptions"
        :model-value="draftSelectedOptions"
        @update:model-value="updateDraftSelection($event)"
      />
    </div>

    <div v-if="connections.length > 0" class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>{{ trans("evaluation.tableColumnCenter") }}</th>
            <th>{{ trans("admin.canRegister") }}</th>
          </tr>
        </thead>
        <transition-group tag="tbody" name="connected-center-row">
          <tr
            v-for="connection in connections"
            :key="connection.center.id"
            v-tooltip="
              isReadOnly(connection.center.id)
                ? trans('users.connectedCenterOutsideOrganisation')
                : null
            "
            class="connected-center-row"
            :data-test="'connected-center-row-' + connection.center.id"
          >
            <td class="centerCell">{{ connection.center.name }}</td>
            <td>
              <DerToggle
                :model-value="connection.is_editable"
                :disabled="isReadOnly(connection.center.id)"
                :class="{
                  'connected-center-toggle-flash': flashedIds.includes(
                    connection.center.id,
                  ),
                }"
                :data-test="'connected-center-toggle-' + connection.center.id"
                @update:model-value="toggleEditable(connection, $event)"
              />
            </td>
          </tr>
        </transition-group>
      </table>
    </div>
  </div>
</template>

<script>
import DerToggle from "../../design-system/DerToggle.vue";
import Notifications from "../Utils/notifications";
import {
  userConnectedCentersGet,
  userConnectedCentersSync,
} from "../../store/core/users";

export default {
  components: {
    DerToggle,
  },
  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
    // Centres in the caller's scope (organisation), shape { id, name }.
    scopeCenters: {
      type: Array,
      default: () => [],
    },
    // True for super admins, who may manage every connection regardless of
    // organisation. When true, no connection is read-only and the multiselect
    // offers the currently connected centres alongside the scope centres.
    unrestrictedScope: {
      type: Boolean,
      default: false,
    },
    // Organisation (region) the connections are managed within. Sent to the
    // server so it scopes the sync to this organisation; null on the
    // Dermicus-admin surface, which manages connections unrestricted.
    regionId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ["editingChange"],
  data() {
    return {
      connections: [],
      primaryCenterId: null,
      isEditing: false,
      draftSelectedIds: [],
      flashedIds: [],
      saving: false,
      loaded: false,
    };
  },
  computed: {
    scopeCenterIds() {
      return this.scopeCenters.map((center) => center.id);
    },
    // Editing before the connections and the scope centres have arrived would
    // build an empty draft, and saving it removes every connection in scope
    // without the removal confirmation ever appearing.
    canManage() {
      if (!this.loaded) {
        return false;
      }
      return this.unrestrictedScope || this.scopeCenters.length > 0;
    },
    // Connections that are inside the caller's scope can be edited/removed.
    inScopeConnections() {
      if (this.unrestrictedScope) {
        return this.connections;
      }
      return this.connections.filter((connection) =>
        this.scopeCenterIds.includes(connection.center.id),
      );
    },
    // Options for the multiselect: the scope centres plus any in-scope centre
    // already connected (so it can be deselected to remove), minus the primary
    // centre (which can never be a secondary connection).
    selectableOptions() {
      const options = new Map();
      this.scopeCenters.forEach((center) =>
        options.set(center.id, { id: center.id, name: center.name }),
      );
      this.inScopeConnections.forEach((connection) =>
        options.set(connection.center.id, {
          id: connection.center.id,
          name: connection.center.name,
        }),
      );
      options.delete(this.primaryCenterId);
      return [...options.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    // The selected options as objects, so vue-select renders labelled chips.
    draftSelectedOptions() {
      return this.draftSelectedIds
        .map((id) => this.selectableOptions.find((option) => option.id === id))
        .filter(Boolean);
    },
    // The dropdown only offers centres that are not already selected; a centre
    // returns here as soon as it is unselected.
    availableOptions() {
      return this.selectableOptions.filter(
        (option) => !this.draftSelectedIds.includes(option.id),
      );
    },
  },
  watch: {
    userId() {
      this.loadConnections();
    },
    isEditing(value) {
      this.$emit("editingChange", value);
    },
  },
  created() {
    this.loadConnections();
  },
  methods: {
    loadConnections() {
      if (!this.userId) {
        return;
      }
      userConnectedCentersGet(this.userId)
        .then((data) => {
          this.connections = data.connected ?? [];
          this.primaryCenterId = data.primary?.center?.id ?? null;
          this.loaded = true;
        })
        .catch((error) => {
          this.userErrorMessage(error);
        });
    },
    isReadOnly(centerId) {
      if (this.unrestrictedScope) {
        return false;
      }
      return !this.scopeCenterIds.includes(centerId);
    },
    startEditing() {
      if (!this.canManage) {
        return;
      }
      this.draftSelectedIds = this.inScopeConnections.map(
        (connection) => connection.center.id,
      );
      this.isEditing = true;
    },
    cancelEditing() {
      this.isEditing = false;
      this.draftSelectedIds = [];
    },
    updateDraftSelection(selection) {
      this.draftSelectedIds = selection.map((entry) =>
        typeof entry === "object" ? entry.id : entry,
      );
    },
    // Build the full desired in-scope set for the PUT, preserving each
    // existing connection's is_editable and defaulting new ones to true.
    buildPayloadFromDraft() {
      const existingById = new Map(
        this.connections.map((connection) => [
          connection.center.id,
          connection.is_editable,
        ]),
      );

      return this.draftSelectedIds.map((centerId) => ({
        center_id: centerId,
        is_editable: existingById.get(centerId) ?? true,
      }));
    },
    buildPayloadFromConnections() {
      return this.inScopeConnections.map((connection) => ({
        center_id: connection.center.id,
        is_editable: connection.is_editable,
      }));
    },
    saveSelection() {
      if (this.saving || !this.canManage) {
        return;
      }

      const currentInScopeIds = this.inScopeConnections.map(
        (connection) => connection.center.id,
      );
      const removedIds = currentInScopeIds.filter(
        (id) => !this.draftSelectedIds.includes(id),
      );

      if (removedIds.length > 0) {
        this.$vueAlert
          .confirm(undefined, undefined, undefined, {
            html: this.trans("users.connectedCentersRemovalWarning"),
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
          })
          .then(() => this.persistDraft())
          .catch(() => false);
      } else {
        this.persistDraft();
      }
    },
    persistDraft() {
      const previousConnections = this.connections;
      const addedIds = this.draftSelectedIds.filter(
        (id) =>
          !this.connections.some((connection) => connection.center.id === id),
      );

      const payload = this.buildPayloadFromDraft();

      // Reconcile the visible list optimistically (preserve out-of-scope rows).
      const outOfScope = this.connections.filter((connection) =>
        this.isReadOnly(connection.center.id),
      );
      const draftRows = this.draftSelectedIds.map((centerId) => {
        const existing = this.connections.find(
          (connection) => connection.center.id === centerId,
        );
        if (existing) {
          return existing;
        }
        const center = this.scopeCenters.find((c) => c.id === centerId);
        return {
          center: { id: center.id, name: center.name },
          is_editable: true,
        };
      });
      this.connections = [...outOfScope, ...draftRows];

      this.put(payload)
        .then(() => {
          this.isEditing = false;
          this.draftSelectedIds = [];
          Notifications.saveSuccess();
          this.flash(addedIds);
        })
        .catch((error) => {
          this.connections = previousConnections;
          this.userErrorMessage(error);
        });
    },
    toggleEditable(connection, value) {
      if (this.saving || this.isReadOnly(connection.center.id)) {
        return;
      }

      const previousValue = connection.is_editable;
      connection.is_editable = value;

      this.put(this.buildPayloadFromConnections())
        .then(() => {
          Notifications.saveSuccess();
        })
        .catch((error) => {
          connection.is_editable = previousValue;
          this.userErrorMessage(error);
        });
    },
    put(centers) {
      this.saving = true;
      return userConnectedCentersSync(
        this.userId,
        centers,
        this.regionId,
      ).finally(() => {
        this.saving = false;
      });
    },
    flash(ids) {
      if (ids.length === 0) {
        return;
      }
      this.flashedIds = ids;
      window.setTimeout(() => {
        this.flashedIds = [];
      }, 5000);
    },
  },
};
</script>

<style lang="scss" scoped>
@use "/resources/assets/sass/abstracts/variables";

.connected-center-row-enter-active,
.connected-center-row-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.connected-center-row-enter-from,
.connected-center-row-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.connected-center-toggle-flash {
  animation: connected-center-flash 1s ease-in-out 5;
}

@keyframes connected-center-flash {
  0%,
  100% {
    filter: none;
  }
  50% {
    filter: drop-shadow(0 0 5px rgba(variables.$dermicusBlue, 0.8));
  }
}
</style>
